/* ============================================================================
   Color Match Utilities — AP Uniforms
   Shared color-matching logic used by look-color-sync.js and theme.js.
   Eliminates duplication that caused 8 bug tickets (#67, #71, #76, #86,
   #101, #135, #141, #193). See #227.

   IMPORTANT — Price Unit Convention:
   All functions return RAW variant objects. Callers handle price formatting:
     - look-color-sync.js uses parseFloat(v.price)  → dollars (product JSON)
     - theme.js uses v.price / 100                   → cents (Recommendations API)
   This utility has ZERO knowledge of price units.
   ============================================================================ */
(function() {
  'use strict';

  /**
   * Normalize a color name for comparison.
   * Strips vendor-specific parenthetical codes: "Ceil (CEI)" → "CEIL"
   * Resolves canonical color group via AP_COLOR_MAP so aliases match.
   *   normalize("ROYAL")      → canonical: #2855C5 (Royal Blue group)
   *   normalize("ROYAL BLUE") → canonical: #2855C5 (same group)
   *   normalize("Galaxy")     → canonical: #2855C5 (also Royal Blue group)
   * Falls back to upper string if AP_COLOR_MAP doesn't recognize the color.
   * Optional Option-A enhancement; #278/#279 fix unaffected when AP_COLOR_MAP
   * isn't available.
   *
   * @param {string} colorName — raw color name from variant option1 or image alt
   * @returns {{ upper: string, stripped: string, canonical: string }}
   */
  function normalizeColor(colorName) {
    var upper = (colorName || '').toUpperCase();
    var stripped = upper.split(' (')[0];
    var canonical = upper;
    // #295: prefer AP_COLOR_MAP.resolveGroup (group NAME) over getHex (override hex)
    // because per-alias overrides can differ even within the same group:
    //   getHex('CARIBBEAN')      = '#024B52'  (override)
    //   getHex('CARIBBEAN BLUE') = '#4A7A8E'  (master group hex) — these don't match!
    //   resolveGroup both → 'Caribbean Blue' — these DO match.
    // Group is the invariant identity defined in data/color-registry.json.
    // Falls back to getHex (legacy #279 behavior) if resolveGroup isn't available
    // (e.g., older color-map.js without the new function).
    try {
      if (typeof window !== 'undefined' && window.AP_COLOR_MAP) {
        if (window.AP_COLOR_MAP.resolveGroup) {
          var g = window.AP_COLOR_MAP.resolveGroup(colorName);
          if (g) canonical = g.toUpperCase();
        } else if (window.AP_COLOR_MAP.getHex) {
          var hex = window.AP_COLOR_MAP.getHex(colorName);
          if (hex) canonical = hex.toUpperCase();
        }
      }
    } catch (e) { /* swallow — fall back to upper */ }
    return { upper: upper, stripped: stripped, canonical: canonical };
  }

  /**
   * True iff two normalized color records refer to the same color group.
   * Internal helper — not exported; use colorsMatch() externally.
   */
  function _eq(a, b) {
    if (a.upper === b.upper) return true;
    if (a.stripped === b.stripped) return true;
    // Canonical compare only when BOTH resolve via AP_COLOR_MAP (i.e., differ
    // from their upper form). Prevents two unmapped colors from accidentally
    // matching just because they both fell back to a string equality test
    // that already failed above.
    if (a.canonical && b.canonical && a.canonical === b.canonical &&
        (a.canonical !== a.upper || b.canonical !== b.upper)) return true;
    return false;
  }

  /**
   * Check if two color names match (exact, stripped, or canonical group).
   * @param {string} color1
   * @param {string} color2
   * @returns {boolean}
   */
  function colorsMatch(color1, color2) {
    return _eq(normalizeColor(color1), normalizeColor(color2));
  }

  /**
   * Find the first AVAILABLE variant matching the given color.
   * Skips variants where available === false (strict equality — #193 fix).
   * Returns the raw variant object so callers can read .id, .price, .option1.
   *
   * @param {Array} variants — array of variant objects with option1, available
   * @param {string} colorName — the target color
   * @returns {Object|null} — the matching variant or null
   */
  function findColorMatchedVariant(variants, colorName) {
    if (!colorName || !variants) return null;
    var norm = normalizeColor(colorName);
    // #295: TWO-PASS — prefer literal-matching variant so card link/price agrees
    // with the literal-matched image (preserves WYSIWYG #147). Fall back to
    // canonical-group match only when no literal variant exists.
    // Pass 1: literal compare (upper or stripped equality)
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.available === false) continue;
      var vc = normalizeColor(v.option1 || '');
      if (vc.upper === norm.upper || vc.stripped === norm.stripped) return v;
    }
    // Pass 2: canonical group match
    for (var j = 0; j < variants.length; j++) {
      var v2 = variants[j];
      if (v2.available === false) continue;
      var vc2 = normalizeColor(v2.option1 || '');
      if (_eq(vc2, norm)) return v2;
    }
    return null;
  }

  /**
   * #282: Find the first AVAILABLE variant whose color belongs to the same
   * master family as the given color. Used when no exact-color variant exists
   * on a target product (e.g., Pacific Blue → Royal Blue family fallback).
   * Returns null if AP_COLOR_MAP isn't loaded, the source color has no family,
   * or no in-stock same-family variant is available.
   *
   * @param {Array} variants
   * @param {string} colorName
   * @returns {Object|null}
   */
  function findFamilyMatchedVariant(variants, colorName) {
    if (!colorName || !variants) return null;
    if (typeof window === 'undefined' || !window.AP_COLOR_MAP || !window.AP_COLOR_MAP.getFamily) return null;
    var srcFam = window.AP_COLOR_MAP.getFamily(colorName);
    if (!srcFam) return null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (v.available === false) continue;
      var vFam = window.AP_COLOR_MAP.getFamily(v.option1 || '');
      if (vFam && vFam === srcFam) return v;
    }
    return null;
  }

  /**
   * Check if ANY variant (regardless of availability) carries this color.
   * Used for OOS-skip logic: product has the color but all variants are OOS.
   *
   * @param {Array} variants
   * @param {string} colorName
   * @returns {boolean}
   */
  function colorExistsInVariants(variants, colorName) {
    if (!colorName || !variants) return false;
    var norm = normalizeColor(colorName);
    for (var i = 0; i < variants.length; i++) {
      if (_eq(normalizeColor(variants[i].option1 || ''), norm)) return true;
    }
    return false;
  }

  /**
   * Find the first image whose alt text matches the given color.
   * Alt text format: "COLOR_NAME - Image N" (e.g., "CEIL (CEI) - Image 1")
   *
   * Resolution priority (#279):
   *   1. Variant→image link (most authoritative — survives any alt-text format change)
   *   2. Alt-text scan, supporting both formats:
   *        - Old: "COLOR - Image N"             (US legacy + AP importer)
   *        - New: "{...} in COLOR - Front View" (SEO Phase 3 rewrite, 2026-05-04)
   *      Within either format, prefer front-view (IMAGE 1 OR FRONT VIEW), fall back
   *      to ANY color-matched image so cards never display the wrong color even
   *      if the front shot is missing.
   *
   * @param {Array} images — array of image objects with .alt, .src, and .id
   * @param {string} colorName — the target color
   * @param {boolean} requireImage1 — if true, prefer 'IMAGE 1' or 'FRONT VIEW' in
   *   alt; otherwise return the first color-matched image.
   *   JS paths (look-color-sync.js, renderRecCard, renderCard) pass true.
   *   Liquid paths (look-card.liquid) don't call this.
   * @param {Array} [variants] — optional variant array. When provided, the
   *   variant→image link is consulted first.
   * @returns {Object|null} — the matching image object or null
   */
  function findColorMatchedImage(images, colorName, requireImage1, variants) {
    if (!colorName || !images || !images.length) return null;
    var norm = normalizeColor(colorName);

    // #295 TWO-PASS: literal first (Pass A), canonical second (Pass B).
    // Each pass tries: variant→image link, then alt-text scan.
    // This preserves WYSIWYG (#147) — when a literal-color variant has an image,
    // we always pick it before searching for canonical-group matches.

    function variantImage(matchFn) {
      if (!variants || !variants.length) return null;
      for (var vi = 0; vi < variants.length; vi++) {
        var v = variants[vi];
        var vc = normalizeColor(v.option1 || '');
        if (!matchFn(vc, norm)) continue;
        var imgId = (v.featured_image && v.featured_image.id) || v.image_id;
        if (!imgId) continue; // try next variant of same color match
        for (var ii = 0; ii < images.length; ii++) {
          if (images[ii].id === imgId) return images[ii];
        }
      }
      return null;
    }

    function altScan(matchFn) {
      var anyMatch = null;
      for (var i = 0; i < images.length; i++) {
        var alt = (images[i].alt || '').toUpperCase();
        var matched = false;

        // Old format: "COLOR - Image N"
        var altFirst = alt.split(' - ')[0].trim();
        var altNorm = normalizeColor(altFirst);
        if (matchFn(altNorm, norm)) matched = true;

        // New format: "...in COLOR - Front/Side/Back/Detail View"
        if (!matched) {
          var m = alt.match(/\bIN\s+([A-Z][A-Z\s]*?)\s*-\s*(?:FRONT|SIDE|BACK|DETAIL)/);
          if (m) matched = matchFn(normalizeColor(m[1].trim()), norm);
        }

        if (matched) {
          if (!anyMatch) anyMatch = images[i];
          if (!requireImage1 || alt.indexOf('IMAGE 1') !== -1 || alt.indexOf('FRONT VIEW') !== -1) {
            return images[i];
          }
        }
      }
      return anyMatch;
    }

    var literalEq = function(a, b) { return a.upper === b.upper || a.stripped === b.stripped; };

    // Pass A: literal — variant link, then alt scan
    var hit = variantImage(literalEq);
    if (hit) return hit;
    hit = altScan(literalEq);
    if (hit) return hit;

    // Pass B: canonical — variant link, then alt scan (only when literal failed entirely)
    hit = variantImage(_eq);
    if (hit) return hit;
    hit = altScan(_eq);
    if (hit) return hit;

    // 3. #282 family alt-text scan — when neither exact variant→image link nor
    // exact alt-text match is found, fall back to a same-family alt match. Lets
    // a Pacific Blue source color find a Royal Blue alt on a target product
    // that doesn't carry Pacific Blue at all.
    if (typeof window !== 'undefined' && window.AP_COLOR_MAP && window.AP_COLOR_MAP.getFamily) {
      var srcFam = window.AP_COLOR_MAP.getFamily(colorName);
      if (srcFam) {
        var anyFamMatch = null;
        for (var fi = 0; fi < images.length; fi++) {
          var falt = (images[fi].alt || '').toUpperCase();
          var fmatched = false;

          var faltFirst = falt.split(' - ')[0].trim();
          var faltFam = window.AP_COLOR_MAP.getFamily(faltFirst);
          if (faltFam && faltFam === srcFam) fmatched = true;

          if (!fmatched) {
            var fm = falt.match(/\bIN\s+([A-Z][A-Z\s]*?)\s*-\s*(?:FRONT|SIDE|BACK|DETAIL)/);
            if (fm) {
              var fmFam = window.AP_COLOR_MAP.getFamily(fm[1].trim());
              fmatched = !!(fmFam && fmFam === srcFam);
            }
          }

          if (fmatched) {
            if (!anyFamMatch) anyFamMatch = images[fi];
            if (!requireImage1 || falt.indexOf('IMAGE 1') !== -1 || falt.indexOf('FRONT VIEW') !== -1) {
              return images[fi];
            }
          }
        }
        if (anyFamMatch) return anyFamMatch;
      }
    }

    return null;
  }

  /**
   * Score a product's color match against the given color.
   * 5-tier system (#282 — extended from 3-tier):
   *   0 = has IN-STOCK variant in the color (ideal)
   *   1 = has IN-STOCK variant in same FAMILY (NEW — color-family fallback)
   *   2 = has the color but all variants OOS         (was 1)
   *   3 = has same-family variant but all OOS        (NEW)
   *   4 = product doesn't carry the color or family  (was 2)
   *
   * theme.js renderRecs adds +4 for cross-gender results on top of this score
   * (preserved). look-color-sync.js has its own cardColorTier() with cold-cache
   * fallback that also delegates here when the product cache is warm.
   *
   * @param {Array} variants — array of variant objects
   * @param {string} colorName — the target color
   * @returns {number} — 0, 1, 2, 3, or 4
   */
  function colorMatchTier(variants, colorName) {
    if (!colorName) return 0; // no color filter — everything matches
    var norm = normalizeColor(colorName);
    var srcFam = (typeof window !== 'undefined' && window.AP_COLOR_MAP && window.AP_COLOR_MAP.getFamily)
      ? window.AP_COLOR_MAP.getFamily(colorName) : null;

    var hasExactInStock = false, hasExactOOS = false;
    var hasFamilyInStock = false, hasFamilyOOS = false;

    for (var i = 0; i < (variants || []).length; i++) {
      var v = variants[i];
      var avail = v.available !== false;
      var vNorm = normalizeColor(v.option1 || '');
      if (_eq(vNorm, norm)) {
        if (avail) hasExactInStock = true; else hasExactOOS = true;
        continue;
      }
      if (srcFam) {
        var vFam = window.AP_COLOR_MAP.getFamily(v.option1 || '');
        if (vFam && vFam === srcFam) {
          if (avail) hasFamilyInStock = true; else hasFamilyOOS = true;
        }
      }
    }
    if (hasExactInStock) return 0;
    if (hasFamilyInStock) return 1;
    if (hasExactOOS) return 2;
    if (hasFamilyOOS) return 3;
    return 4;
  }

  /**
   * Find the first available variant regardless of color.
   * Used as fallback when the selected color isn't found.
   *
   * @param {Array} variants
   * @returns {Object|null} — the first available variant or null
   */
  function findFallbackVariant(variants) {
    if (!variants) return null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available !== false) {
        return variants[i];
      }
    }
    return null;
  }

  // Expose as a global for look-color-sync.js and theme.js
  window.AP_COLOR_MATCH = {
    normalizeColor: normalizeColor,
    colorsMatch: colorsMatch,
    findColorMatchedVariant: findColorMatchedVariant,
    findFamilyMatchedVariant: findFamilyMatchedVariant,
    colorExistsInVariants: colorExistsInVariants,
    findColorMatchedImage: findColorMatchedImage,
    colorMatchTier: colorMatchTier,
    findFallbackVariant: findFallbackVariant
  };
})();
